import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { Check, Clock, FileText, Info, Maximize2, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/feedback/empty-state";
import { Cabecera } from "@/components/propuesta/cabecera";
import { Aviso, Caja, Estado } from "@/components/propuesta/piezas";
import { type Papel, Visor } from "@/components/propuesta/visor";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { comoEstaLaCuenta, filasDelVoucher } from "@/lib/voucher";

export const Route = createFileRoute("/_app/movimiento/$id")({
  component: Ficha,
});

type Candidato = {
  id: string;
  comprobanteId: string | null;
  operacion: string | null;
  fecha: string | null;
  importe: number | null;
  moneda: "PEN" | "USD" | null;
  comprobante: string | null;
  cliente: string | null;
  /** De qué banco salió el papel. Sale de la lectura, no de `banco_destino`. */
  banco: string | null;
  medio: string | null;
  origen: string | null;
  hora: string | null;
  pagador: string | null;
  cuentaDestino: string | null;
  /** La cuenta resuelta contra el maestro. `null` = no es del grupo. */
  cuentaRegistrada: {
    banco: string;
    numero: string;
    moneda: "PEN" | "USD";
    razonSocial: string | null;
  } | null;
  distancia: number | null;
  /** Días entre el voucher y el abono. Negativo si el voucher es posterior. */
  diasDeDiferencia: number | null;
  /** Si el motor lo propuso, o si es algo que se está rebuscando a mano. */
  deElMotor: boolean;
  comparacion: "identico" | "mismo_final" | "difieren" | "no_comparable";
  recorte: string | null;
  /** Dónde está la boleta emitida, y cuántas hojas trae su expediente. */
  documento: {
    archivoId: string;
    pagina: number;
    paginas: number | null;
  } | null;
  /** Si esa boleta ya la cobró otro abono. */
  yaTomada: boolean;
  /** Si la cobró ESTE abono: el candidato que se acabó eligiendo. */
  esElConciliado: boolean;
};

/** Un cruce ya hecho: contra qué boleta se conció este abono, y con qué. */
type Conciliado = {
  comprobanteId: string;
  boleta: string | null;
  cliente: string | null;
  importe: number;
  moneda: "PEN" | "USD";
  totalComprobante: number | null;
  voucherId: string | null;
  recorte: string | null;
  importeVoucher: number | null;
  regla: string;
  confianza: string;
  puntaje: number;
  origen: string;
  quien: string | null;
  cuando: string | null;
  documento: {
    archivoId: string;
    pagina: number;
    paginas: number | null;
  } | null;
};

// ===========================================================================
// La ficha de un movimiento (lámina 3).
//
// Ficha de objeto de SAP Fiori: cabecera fija con lo que identifica el
// movimiento, la barra de etapas, pestañas, y el cuerpo en dos columnas —
// a la izquierda lo que hay que decidir, a la derecha la prueba y quién dijo
// qué.
//
// La pieza central es «lo que dice cada lado»: el voucher y el extracto
// enfrentados campo por campo. Decidir es comparar dos papeles, y ponerlos uno
// al lado del otro es literalmente el trabajo.
//
// LO QUE SE ENSEÑA DEPENDE DE SI YA SE DECIDIÓ, y antes no.
//
// Esta pantalla ponía en «el voucher del cliente» el primer elemento de
// `candidatos` —una consulta por importe sin orden— tanto si el abono estaba
// por decidir como si llevaba semanas cerrado. En cuatro de cada cinco
// conciliados, la boleta que enseñaba no era contra la que se había
// conciliado. Y encima avisaba de que «hay N vouchers con este mismo importe»
// sobre una ambigüedad que alguien ya había resuelto: salía en 79 de los 192
// cerrados.
//
// Ahora el servidor manda `conciliado` —lo que se decidió, con su regla y su
// firma— aparte de `candidatos` —lo que se propone—. Si hay decisión, manda la
// decisión, y los candidatos bajan a ser lo que el área pidió: información
// adicional sobre qué otras cosas pudieron haber sido.
// ===========================================================================

/**
 * El recorrido, con la clave que manda el servidor.
 *
 * Antes la etapa se calculaba aquí con un ternario —`conciliado ? 4 : 3`—, así
 * que la barra pintaba seis y distinguía dos. Ahora la dice el servidor, que
 * es quien sabe si el cruce encontró candidatos y si el periodo se cerró.
 */
const ETAPAS = [
  { clave: "recibido", nombre: "Recibido" },
  { clave: "leido", nombre: "Leído" },
  { clave: "cruzado", nombre: "Cruzado" },
  { clave: "por_decidir", nombre: "Por decidir" },
  { clave: "conciliado", nombre: "Conciliado" },
  { clave: "cerrado", nombre: "Cerrado" },
] as const;

function Ficha() {
  const { id } = useParams({ from: "/_app/movimiento/$id" });
  const { data, isPending } = api.ficha.useQuery(["ficha", id], {
    params: { id },
  });
  const d = data?.status === 200 ? data.body : null;

  // Quién decidió qué sobre este movimiento. Va en su propia consulta y no
  // dentro de la ficha porque cambia por su cuenta —cada vez que alguien
  // decide— y porque la ficha se mira mucho más de lo que se decide.
  const { data: seg } = api.seguimiento.useQuery(["seguimiento", id], {
    params: { movimientoId: id },
  });
  const historia = seg?.status === 200 ? seg.body.entradas : [];

  // El papel que se está mirando en grande. Sustituye a los dos interruptores
  // que enseñaban la imagen debajo del panel, a la anchura de una columna
  // estrecha — que es donde menos se lee un voucher de móvil.
  const [papel, setPapel] = useState<Papel | null>(null);
  const [buscando, setBuscando] = useState(false);
  const [nota, setNota] = useState("");
  const [texto, setTexto] = useState("");
  const qc = useQueryClient();
  const decidir = api.decidir.useMutation();
  const anotar = api.anotar.useMutation();
  // Quitar la conciliación para poder rectificarla. Vale para lo que ató el
  // motor —que es el 93 %— y no solo para lo que decidió una persona.
  const desvincular = api.desvincular.useMutation();
  const quitarLaConciliacion = () => {
    if (desvincular.isPending) return;
    desvincular.mutate(
      { body: { movimientoId: id } },
      {
        onSuccess: (r: {
          status: number;
          body: { dice?: string; message?: string };
        }) => {
          if (r.status !== 200) {
            toast.error(r.body.message ?? "No se pudo desvincular");
            return;
          }
          toast.success(r.body.dice ?? "Desvinculado");
          qc.invalidateQueries();
        },
        onError: () => toast.error("No se pudo desvincular"),
      },
    );
  };
  const guardarNota = () => {
    if (!nota.trim()) return;
    anotar.mutate(
      { body: { movimientoId: id, nota } },
      {
        onSuccess: () => {
          setNota("");
          toast.success("Anotado en el expediente");
          qc.invalidateQueries();
        },
        onError: () => toast.error("No se pudo anotar"),
      },
    );
  };
  const responder = (
    respuesta: "si" | "no" | "no_se",
    comprobanteId: string | null,
  ) => {
    decidir.mutate(
      {
        body: {
          movimientoId: id,
          // La misma boleta que se está enseñando en «lo que dice cada lado»,
          // pasada desde el botón: si la leyera de una variable de más arriba,
          // bastaría reordenar el componente para que se firmara otra.
          comprobanteId,
          respuesta,
        },
      },
      {
        onSuccess: (r: { status: number; body: { dice?: string } }) => {
          if (r.status !== 200) {
            toast.error("No se pudo guardar la decisión");
            return;
          }
          toast.success(r.body.dice ?? "Guardado");
          qc.invalidateQueries();
        },
        onError: () => toast.error("No se pudo guardar la decisión"),
      },
    );
  };

  if (isPending) {
    return (
      <>
        <Cabecera migas={["Conciliación", "Por decidir"]} titulo="Movimiento" />
        <Skeleton className="h-32 w-full" />
      </>
    );
  }

  if (!d) {
    return (
      <>
        <Cabecera migas={["Conciliación"]} titulo="Movimiento" />
        <EmptyState
          title="Ese movimiento no existe"
          message="Puede que se haya borrado, o que el enlace sea de un periodo que ya se vació."
          action={
            <Button variant="outline" asChild>
              <Link to="/movimientos">Ver la mesa de trabajo</Link>
            </Button>
          }
        />
      </>
    );
  }

  const etapaActual = Math.max(
    0,
    ETAPAS.findIndex((e) => e.clave === d.etapa),
  );
  const candidatos = d.candidatos as Candidato[];
  const conciliado = d.conciliado as Conciliado[];
  const yaSeDecidio = conciliado.length > 0;
  // Los que NO se eligieron. Es lo que el área pidió ver «como información
  // adicional»: qué otras cosas pudieron haber sido.
  const losOtros = candidatos.filter((c) => !c.esElConciliado);
  // El voucher que manda: el que se usó si ya se decidió, y si no el más
  // cercano en fecha. Toda la pantalla tiraba de `candidatos[0]`.
  const elPapel = elVoucherQueManda(conciliado, candidatos);
  // Si esa cuenta es del grupo o no. Se calcula una vez y no tres veces dentro
  // del JSX.
  const laCuenta = elPapel ? comoEstaLaCuenta(elPapel) : null;
  const exacto = candidatos.find((c) => c.comparacion === "identico");
  const casiExacto = candidatos.find((c) => c.comparacion === "mismo_final");

  return (
    <>
      <Cabecera
        // La miga dice en qué bandeja está ESTE movimiento, no la de donde se
        // vino: se llega aquí por enlace directo tan a menudo como desde la
        // lista, y «Por decidir» encima de uno ya conciliado hace dudar de cuál
        // de las dos cosas es la verdadera.
        migas={["Conciliación", d.estadoEtiqueta]}
        titulo={
          d.operacion ? `Operación ${d.operacion}` : "Movimiento del extracto"
        }
        acciones={
          <>
            {/* Sobre un abono ya cerrado, «confirmar» no tiene sentido: lo que
                se quiere es rectificar, y eso es desvincular. El botón se
                escondía detrás de la misma etiqueta y conciliaba contra
                `candidatos[0]`, que no tenía por qué ser el suyo. */}
            {/* Sobre un abono cerrado lo que se quiere es rectificar. Antes
                aquí no había nada: «Deshacer» solo existía en la revisión
                guiada y solo para lo que uno acababa de decidir en esa
                sesión. */}
            {yaSeDecidio ? (
              <Button
                size="sm"
                variant="outline"
                className="border-red-500/40 text-red-600 hover:bg-red-500/10 dark:text-red-400"
                disabled={desvincular.isPending}
                onClick={quitarLaConciliacion}
              >
                <X className="mr-2 size-4" />
                {desvincular.isPending ? "Quitando…" : "Desvincular"}
              </Button>
            ) : null}
            {yaSeDecidio ? null : (
              <Button
                size="sm"
                disabled={candidatos.length === 0 || decidir.isPending}
                onClick={() =>
                  responder("si", candidatos[0]?.comprobanteId ?? null)
                }
              >
                <Check className="mr-2 size-4" />
                Confirmar y conciliar
              </Button>
            )}
            <Button
              size="sm"
              variant={buscando ? "secondary" : "outline"}
              onClick={() => setBuscando((v) => !v)}
            >
              {buscando ? "Dejar de buscar" : "Elegir otra boleta"}
            </Button>
            <Button size="sm" variant="ghost" asChild>
              <Link to="/movimientos">Volver a la mesa</Link>
            </Button>
          </>
        }
      />

      {buscando && d ? (
        <BuscadorDeBoletas
          importe={d.importe}
          moneda={d.moneda}
          texto={texto}
          alEscribir={setTexto}
          alElegir={(comprobanteId) => {
            responder("si", comprobanteId);
            setBuscando(false);
          }}
          inhabilitado={decidir.isPending}
        />
      ) : null}

      {/* La barra de etapas: dónde está y cuánto le falta. */}
      <div className="flex flex-wrap items-center gap-1 text-[12px]">
        {ETAPAS.map((e, i) => (
          <span
            key={e.clave}
            className={cn(
              "rounded-md border px-3 py-1.5",
              i === etapaActual
                ? "border-cyan-500 bg-cyan-500 font-semibold text-cyan-950"
                : i < etapaActual
                  ? "border-border bg-muted text-foreground"
                  : "border-dashed text-muted-foreground",
            )}
          >
            {e.nombre}
          </span>
        ))}
      </div>

      {/* Cabecera del objeto: el importe manda, y al lado los datos que
          deciden. */}
      <div className="flex flex-wrap items-start justify-between gap-6 border-b pb-4">
        <div className="min-w-0">
          <div className="font-bold text-4xl tabular-nums tracking-tight">
            {dinero(d.importe, d.moneda)}
          </div>
          <p className="mt-1 text-[13px] text-muted-foreground">
            Abono en {d.banco} {d.moneda === "PEN" ? "soles" : "dólares"}
            {d.cuenta ? ` · cuenta ${d.cuenta}` : ""}
            {d.empresa ? ` · ${d.empresa}` : ""} · {fecha(d.fecha)}
          </p>
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            <Estado tono={d.estadoTono}>{d.estadoEtiqueta}</Estado>
          </div>
        </div>

        <div className="flex flex-wrap gap-x-10 gap-y-3">
          <Dato titulo="N.º operación (extracto)" valor={d.operacion ?? "—"} />
          {/* El total, no los doce que caben. Decir «12» cuando hay 25 hace
              creer que se está viendo la lista entera. */}
          <Dato
            titulo="Candidatos con ese importe"
            valor={(d.candidatosTotales ?? candidatos.length).toLocaleString(
              "es-PE",
            )}
            tono={
              yaSeDecidio
                ? "ok"
                : d.candidatosTotales === 0
                  ? "bad"
                  : d.candidatosTotales === 1
                    ? "ok"
                    : "warn"
            }
          />
          <Dato
            titulo="Distancia al más cercano"
            valor={
              candidatos.length === 0
                ? "—"
                : exacto
                  ? "0 · idéntico"
                  : casiExacto
                    ? "mismo final"
                    : (() => {
                        const ds = candidatos
                          .map((c) => c.distancia)
                          .filter((x): x is number => x !== null);
                        return ds.length
                          ? `${Math.min(...ds)} dígitos`
                          : "no comparable";
                      })()
            }
            tono={exacto ? "ok" : casiExacto ? "warn" : "bad"}
          />
          <Dato titulo="Antigüedad" valor={`${d.antiguedadDias} días`} />
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.6fr_1fr]">
        {/* ---- Izquierda: lo que hay que decidir --------------------- */}
        <div className="space-y-4">
          {/* EL AVISO DE AMBIGÜEDAD, SOLO CUANDO HAY AMBIGÜEDAD.
              Antes se pintaba mirando únicamente cuántos candidatos había, sin
              mirar si alguien ya había decidido: salía en 79 de los 192
              movimientos conciliados, avisando de una duda ya resuelta. Un
              aviso que sale cuando no toca enseña a ignorarlos todos. */}
          {yaSeDecidio ? null : candidatos.length === 0 ? (
            <Aviso tono="warn">
              <Info className="mt-px size-4 shrink-0" />
              <span>
                <b>Ningún voucher leído tiene este importe.</b> O el pago no
                está entre los documentos cargados, o su voucher todavía no se
                ha leído.
              </span>
            </Aviso>
          ) : d.candidatosTotales > 1 ? (
            <Aviso tono="warn">
              <Info className="mt-px size-4 shrink-0" />
              <span>
                <b>
                  Hay {d.candidatosTotales} vouchers con este mismo importe.
                </b>{" "}
                El importe solo no alcanza para decidir: hace falta mirar el
                número de operación y la fecha. Van ordenados por cercanía de
                fecha al abono.
              </span>
            </Aviso>
          ) : null}

          {/* LO QUE SE DECIDIÓ, cuando se decidió. Es lo primero que se quiere
              ver al abrir un abono cerrado, y era justo lo que no se veía. */}
          {yaSeDecidio ? (
            <Caja
              titulo={
                conciliado.length === 1
                  ? "Conciliado con esta boleta"
                  : `Conciliado con ${conciliado.length} boletas`
              }
              derecha={
                conciliado.length > 1 ? "el pago cubre varias" : undefined
              }
            >
              <div className="space-y-3">
                {conciliado.map((c) => (
                  <ElCruce key={c.comprobanteId} c={c} abono={d.importe} />
                ))}
              </div>
            </Caja>
          ) : null}

          <Caja
            titulo={
              yaSeDecidio ? "Lo que decía cada lado" : "Lo que dice cada lado"
            }
            derecha="el extracto manda sobre el voucher"
          >
            <div className="grid gap-4 md:grid-cols-2">
              {/* El voucher que se usó si ya se decidió; y si no, el candidato
                  más cercano en fecha. Antes era siempre `candidatos[0]` de una
                  lista sin ordenar: en 4 de cada 5 conciliados, otro. */}
              <Lado
                titulo={
                  yaSeDecidio
                    ? "El voucher con el que se ató"
                    : "Voucher del cliente"
                }
                /* Las mismas ocho filas que la revisión guiada, dichas por
                   `filasDelVoucher`. Esta pantalla enseñaba cuatro —importe,
                   fecha, operación y un «banco de destino» que salía de una
                   columna escrita en 2 de 144 filas—, así que quien llegaba
                   aquí veía menos que quien llegaba a la revisión, y de un
                   dato distinto. */
                filas={
                  elPapel
                    ? [
                        ...filasDelVoucher({
                          importe: elPapel.importe,
                          moneda: elPapel.moneda,
                          cuando: elPapel.fecha,
                          hora: elPapel.hora,
                          operacion: elPapel.operacion,
                          medio: elPapel.medio,
                          origen: elPapel.origen,
                          banco: elPapel.banco,
                          pagador: elPapel.pagador,
                          cuentaDestino: elPapel.cuentaDestino,
                          cuentaRegistrada: elPapel.cuentaRegistrada,
                        }),
                        // La distancia al abono, que es lo que más pesa y no se
                        // enseñaba en ninguna parte. Va aquí y no en la lista
                        // compartida porque no es un dato del voucher: es la
                        // relación entre el voucher y ESTE abono.
                        [
                          "Distancia al abono",
                          elPapel.diasDeDiferencia === null
                            ? "—"
                            : enDias(elPapel.diasDeDiferencia),
                        ],
                      ]
                    : [["", "Sin voucher que lo explique"]]
                }
              />
              <Lado
                titulo="Estado de cuenta del banco"
                filas={[
                  ["Importe", dinero(d.importe, d.moneda)],
                  ["Fecha de operación", fecha(d.fecha)],
                  ["N.º operación", d.operacion ?? "—"],
                  ["Descripción", d.descripcion],
                ]}
              />
            </div>

            {/* SI LA CUENTA A LA QUE ENTRÓ EL DINERO ES DEL GRUPO.
                Lo pidió contabilidad —«si está registrado o no está registrado
                en la empresa»— y ya estaba resuelto contra el maestro en la
                base: de 92 cuentas leídas, 65 resuelven y 27 no. No llegaba a
                ninguna de las dos pantallas. */}
            {laCuenta ? (
              <p
                className={cn(
                  "mt-3 rounded px-2 py-1.5 text-[12px]",
                  laCuenta.tono === "ok"
                    ? "bg-emerald-600/10 text-emerald-700 dark:text-emerald-400"
                    : "bg-amber-500/15 text-amber-700 dark:text-amber-400",
                )}
              >
                {laCuenta.texto}
              </p>
            ) : null}

            <div className="mt-4 flex flex-wrap gap-2">
              {candidatos[0] ? (
                <>
                  <Estado tono="ok">Importe y moneda coinciden</Estado>
                  {exacto ? (
                    <Estado tono="ok">El número de operación coincide</Estado>
                  ) : (
                    <Estado tono="warn">
                      El número de operación no coincide
                    </Estado>
                  )}
                </>
              ) : (
                <Estado tono="bad">No hay con qué compararlo</Estado>
              )}
            </div>
          </Caja>

          {/* LOS QUE PUDIERON HABER SIDO.
              Cuando el abono ya está cerrado esto no es una lista de opciones:
              es información adicional —«cuáles hubieran sido los otros
              candidatos»—, que es como lo pidió contabilidad. Por eso cambia de
              título y se aparta en gris en vez de competir con la decisión.

              Ordenados por CERCANÍA DE FECHA, no por lo que devuelva Postgres.
              De los 8.046 pares que casan solo por importe, el 76 % está a más
              de una semana del abono. */}
          <Caja
            titulo={
              yaSeDecidio
                ? `Los otros que pudieron haber sido (${losOtros.length})`
                : `Vouchers con este importe (${d.candidatosTotales})`
            }
            derecha={
              d.candidatosTotales > candidatos.length
                ? `los ${candidatos.length} más cercanos de ${d.candidatosTotales}`
                : "ordenados por cercanía de fecha"
            }
          >
            {(yaSeDecidio ? losOtros : candidatos).length === 0 ? (
              <p className="text-muted-foreground text-sm">
                {yaSeDecidio
                  ? "Ninguno: no había con qué confundirlo."
                  : "Ninguno."}
              </p>
            ) : (
              <table className="w-full text-[12.5px]">
                <thead className="border-b text-[11px] text-muted-foreground uppercase tracking-wide">
                  <tr>
                    <th className="py-2 text-left font-medium">
                      N.º operación
                    </th>
                    <th className="py-2 text-left font-medium">Fecha</th>
                    <th className="py-2 text-right font-medium">Al abono</th>
                    <th className="py-2 text-left font-medium">Boleta</th>
                    <th className="py-2 text-left font-medium">Cliente</th>
                    <th className="py-2 text-right font-medium">Operación</th>
                  </tr>
                </thead>
                <tbody>
                  {(yaSeDecidio ? losOtros : candidatos).map((c) => (
                    <tr
                      key={c.id}
                      className={cn(
                        "border-b last:border-0",
                        c.esElConciliado && "bg-emerald-500/10",
                      )}
                    >
                      {/* Ancho fijo: aquí se comparan números entre sí y la
                          diferencia suele ser de un solo dígito. */}
                      <td className="py-2 font-mono text-[11.5px]">
                        {c.operacion ?? "—"}
                      </td>
                      <td className="py-2 tabular-nums">
                        {c.fecha ? fecha(c.fecha) : "—"}
                      </td>
                      {/* La distancia en días, que es lo que más pesa para
                          decidir y hasta ahora no se comparaba con nada: la
                          fecha se pintaba suelta. */}
                      <td className="py-2 text-right tabular-nums">
                        {c.diasDeDiferencia === null ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <span
                            className={
                              Math.abs(c.diasDeDiferencia) <= 7
                                ? undefined
                                : "text-muted-foreground"
                            }
                          >
                            {enDias(c.diasDeDiferencia)}
                          </span>
                        )}
                      </td>
                      <td className="py-2">
                        {c.comprobante ?? "—"}
                        {c.esElConciliado ? (
                          <span className="ml-1.5 text-[10.5px] text-emerald-700 dark:text-emerald-400">
                            es esta
                          </span>
                        ) : c.yaTomada ? (
                          /* Que sea candidata no significa que esté libre:
                             elegirla sería cobrarla dos veces. */
                          <span className="ml-1.5 text-[10.5px] text-amber-700 dark:text-amber-400">
                            ya cobrada
                          </span>
                        ) : null}
                        {/* De dónde sale: propuesta del cruce, o algo que se
                            está rebuscando por importe. No es lo mismo. */}
                        {c.deElMotor ? (
                          <span className="ml-1.5 text-[10.5px] text-muted-foreground">
                            · la propuso el cruce
                          </span>
                        ) : null}
                      </td>
                      <td
                        className="max-w-[16rem] truncate py-2"
                        title={c.cliente ?? ""}
                      >
                        {c.cliente ?? "—"}
                      </td>
                      <td className="py-2 text-right">
                        <Estado tono={tonoDe(c)}>{comoSeCompara(c)}</Estado>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Caja>
        </div>

        {/* ---- Derecha: la prueba y el rastro ------------------------ */}
        <div className="space-y-4">
          {/* EL PAPEL QUE MANDA, que es el que se usó si ya se decidió.
              Esta caja tiraba de `candidatos[0]` como el resto de la pantalla,
              así que en un abono conciliado enseñaba —y dejaba abrir— la imagen
              de un voucher que no era el suyo. */}
          <Caja
            titulo={
              yaSeDecidio
                ? "El voucher con el que se ató"
                : "El voucher, tal cual llegó"
            }
            derecha={
              yaSeDecidio
                ? "el que se usó"
                : exacto
                  ? "coincide"
                  : "por confirmar"
            }
          >
            {elPapel?.recorte ? (
              <>
                <div className="rounded-md border bg-muted/40 p-3 font-mono text-[11.5px] leading-relaxed">
                  <div>
                    Importe :{" "}
                    {dinero(elPapel.importe ?? 0, elPapel.moneda ?? "PEN")}
                  </div>
                  <div>
                    Fecha : {elPapel.fecha ? fecha(elPapel.fecha) : "—"}
                  </div>
                  <div>N.º operación : {elPapel.operacion ?? "—"}</div>
                  <div>Banco : {elPapel.banco ?? "—"}</div>
                </div>
                {elPapel?.recorte ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3 w-full"
                      onClick={() =>
                        setPapel({
                          clase: "imagen",
                          recorte: elPapel.recorte as string,
                          titulo: "El voucher, tal cual llegó",
                        })
                      }
                    >
                      <Maximize2 className="mr-2 size-4" />
                      Agrandar el voucher
                    </Button>
                    {/* Se abre en el visor y ya no debajo del panel: aquí la
                        imagen salía a la anchura de una columna estrecha, que
                        es donde menos se lee un voucher de móvil. */}
                    <img
                      src={`/imagen/${elPapel.recorte}`}
                      alt="El voucher tal cual llegó"
                      className="mt-3 w-full cursor-zoom-in rounded-md border object-contain"
                      onClick={() =>
                        setPapel({
                          clase: "imagen",
                          recorte: elPapel.recorte as string,
                          titulo: "El voucher, tal cual llegó",
                        })
                      }
                    />

                    {/* El expediente emitido, el otro papel.
                        La pregunta de esta pantalla es la misma que la de la
                        revisión —«¿este abono es el pago de esa boleta?»— y
                        aquí sólo estaba el voucher. Y del expediente se abría
                        una hoja de las cinco que trae de media. */}
                    {elPapel?.documento ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2 w-full"
                        onClick={() =>
                          elPapel.documento &&
                          setPapel({
                            clase: "expediente",
                            archivoId: elPapel.documento.archivoId,
                            pagina: elPapel.documento.pagina,
                            paginas: elPapel.documento.paginas,
                            titulo: `El expediente de ${elPapel.comprobante ?? "la boleta"}`,
                          })
                        }
                      >
                        <FileText className="mr-2 size-4" />
                        {elPapel.documento.paginas &&
                        elPapel.documento.paginas > 1
                          ? `Ver el expediente (${elPapel.documento.paginas} hojas)`
                          : `Abrir la boleta ${elPapel.comprobante ?? ""}`}
                      </Button>
                    ) : null}
                  </>
                ) : (
                  <p className="mt-3 text-[12px] text-muted-foreground">
                    De este pago no se guardó una imagen que mirar.
                  </p>
                )}
              </>
            ) : (
              <p className="text-muted-foreground text-sm">
                Todavía no hay un voucher asociado a este movimiento.
              </p>
            )}
          </Caja>

          <Caja
            titulo="Seguimiento"
            derecha="lo que ha pasado con este movimiento"
          >
            <div className="space-y-3 text-[12.5px]">
              {historia.length === 0 ? (
                <p className="text-muted-foreground">
                  Cuando alguien decida sobre este movimiento, aquí quedará
                  quién fue, cuándo y con qué prueba. Todavía no ha pasado nada.
                </p>
              ) : (
                <ol className="space-y-2.5">
                  {historia.map((h: (typeof historia)[number]) => (
                    <li key={h.id} className="flex gap-2.5">
                      <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-cyan-500" />
                      <span>
                        <b>{h.usuario ?? "alguien"}</b> {h.dice}
                        <span className="block text-[11px] text-muted-foreground">
                          {h.momento}
                        </span>
                      </span>
                    </li>
                  ))}
                </ol>
              )}
              <div className="flex gap-2">
                <input
                  value={nota}
                  onChange={(e) => setNota(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && nota.trim()) guardarNota();
                  }}
                  placeholder="Anotar algo en el expediente…"
                  className="h-[32px] min-w-0 flex-1 rounded-md border bg-background px-2.5 text-[12px] outline-none placeholder:text-muted-foreground"
                />
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!nota.trim() || anotar.isPending}
                  onClick={guardarNota}
                >
                  Anotar
                </Button>
              </div>
            </div>
          </Caja>
        </div>
      </div>

      {/* Barra anclada: lo que se decide y qué implica. */}
      <div className="sticky bottom-0 -mx-4 flex flex-wrap items-center gap-3 border-t bg-background/95 px-4 py-3 backdrop-blur md:-mx-6 md:px-6 lg:-mx-8 lg:px-8">
        <p className="text-[12.5px]">
          Decidís sobre <b>{dinero(d.importe, d.moneda)}</b>
          <span className="text-muted-foreground">
            {" · "}lo que elijas queda firmado con tu nombre y la hora
          </span>
        </p>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={decidir.isPending}
            onClick={() => responder("no_se", null)}
          >
            <Clock className="mr-2 size-4" />
            Dejar para después
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-red-500/40 text-red-600 hover:bg-red-500/10 dark:text-red-400"
            disabled={decidir.isPending}
            onClick={() => responder("no", null)}
          >
            <X className="mr-2 size-4" />
            No es este voucher
          </Button>
          <Button
            size="sm"
            disabled={candidatos.length === 0 || decidir.isPending}
            onClick={() =>
              responder("si", candidatos[0]?.comprobanteId ?? null)
            }
          >
            <Check className="mr-2 size-4" />
            Confirmar y conciliar
          </Button>
        </div>
      </div>

      {/* El papel en grande: un diálogo a pantalla completa, con zoom, giro y
          paso de hoja. */}
      <Visor papel={papel} alCerrar={() => setPapel(null)} />
    </>
  );
}

/**
 * Cómo se compara la operación del voucher con la del extracto.
 *
 * Cuando los números tienen distinto largo no se inventa una distancia: lo
 * único que se puede afirmar es si uno acaba en el otro. Pasa de verdad —el
 * voucher trae «01558418» y el extracto «558418»— y decir «10 dígitos» sería
 * un número sin significado que quien decide se creería.
 */
function comoSeCompara(c: Candidato): string {
  switch (c.comparacion) {
    case "identico":
      return "0 · idéntico";
    case "mismo_final":
      return "acaba igual";
    case "difieren":
      return `${c.distancia} dígito${c.distancia === 1 ? "" : "s"}`;
    default:
      return "no comparable";
  }
}

function tonoDe(c: Candidato): "ok" | "warn" | "neutro" {
  if (c.comparacion === "identico") return "ok";
  if (c.comparacion === "mismo_final") return "warn";
  if (c.comparacion === "difieren" && (c.distancia ?? 9) <= 2) return "warn";
  return "neutro";
}

/**
 * La distancia en días entre el voucher y el abono, dicha en palabras.
 *
 * El signo importa y por eso no se enseña un valor absoluto: un voucher
 * POSTERIOR al abono es sospechoso —pasa en 761 de los 8.046 pares— mientras
 * que uno de dos días antes es lo más normal del mundo, porque una
 * transferencia interbancaria tarda en acreditarse.
 */
function enDias(dias: number): string {
  if (dias === 0) return "el mismo día";
  if (dias > 0) return `${dias} día${dias === 1 ? "" : "s"} antes`;
  return `${-dias} día${dias === -1 ? "" : "s"} después`;
}

/**
 * Qué voucher se enseña enfrentado al extracto.
 *
 * Si el abono ya está conciliado, el que se usó para atarlo — no el primero de
 * la lista de candidatos. Ese era el fallo: `ficha` no leía `cruce`, así que la
 * pantalla enseñaba `candidatos[0]` de una consulta sin `orderBy`, y en cuatro
 * de cada cinco conciliados era otro distinto del suyo.
 *
 * Si no se ha decidido, el candidato más cercano en fecha, que ahora es el
 * primero de verdad.
 */
function elVoucherQueManda(
  conciliado: Conciliado[],
  candidatos: Candidato[],
): Candidato | null {
  const atado = conciliado.find((c) => c.voucherId);
  if (atado) {
    const suyo = candidatos.find((c) => c.id === atado.voucherId);
    if (suyo) return suyo;
  }
  return candidatos[0] ?? null;
}

/**
 * Un cruce ya hecho: contra qué boleta, con qué voucher y quién lo firmó.
 *
 * Enseña los TRES importes cuando no cuadran —lo que entró al banco, lo que
 * dice el voucher y lo que dice la boleta—, que es el cruce que pidió
 * contabilidad al encontrar un voucher de 2 mil sobre una boleta de mil. De 318
 * filas de voucher, 110 no cuadran con el total de su boleta y hasta ahora
 * ninguna pantalla lo decía.
 */
function ElCruce({ c, abono }: { c: Conciliado; abono: number }) {
  const descuadra =
    c.totalComprobante !== null && c.totalComprobante !== c.importe;
  const voucherDescuadra =
    c.importeVoucher !== null && c.importeVoucher !== c.importe;

  return (
    <div className="rounded-lg border p-3">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <b className="text-[13px]">{c.boleta ?? "boleta sin número"}</b>
        <span className="text-[12px] text-muted-foreground">{c.cliente}</span>
        <span className="ml-auto font-semibold tabular-nums">
          {dinero(c.importe, c.moneda)}
        </span>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Estado tono="ok">Conciliado</Estado>
        <span className="rounded border px-1.5 py-0.5 font-mono text-[11px]">
          {c.regla}
        </span>
        <span className="text-[11.5px] text-muted-foreground">
          {c.origen === "usuario"
            ? `lo confirmó ${c.quien ?? "una persona"}`
            : "lo ató el cruce automático"}
          {c.cuando ? ` · ${fecha(c.cuando.slice(0, 10))}` : ""}
          {c.puntaje > 0 ? ` · puntaje ${c.puntaje.toFixed(2)}` : ""}
        </span>
      </div>

      {/* Los tres importes, solo cuando alguno no cuadra. Si los tres son el
          mismo no hace falta decirlo tres veces. */}
      {descuadra || voucherDescuadra ? (
        <div className="mt-2.5 rounded bg-amber-500/15 px-2 py-1.5 text-[12px]">
          Al banco entraron <b>{dinero(abono, c.moneda)}</b>
          {voucherDescuadra ? (
            <>
              , el voucher dice <b>{dinero(c.importeVoucher ?? 0, c.moneda)}</b>
            </>
          ) : null}
          {descuadra ? (
            <>
              {" "}
              y la boleta dice{" "}
              <b>{dinero(c.totalComprobante ?? 0, c.moneda)}</b>
            </>
          ) : null}
          . De este abono se aplicaron <b>{dinero(c.importe, c.moneda)}</b> a
          esta boleta.
        </div>
      ) : null}

      {c.documento || c.recorte ? (
        <div className="mt-2 flex flex-wrap gap-3 text-[12px]">
          {c.documento ? (
            <a
              className="text-cyan-700 hover:underline dark:text-cyan-400"
              href={`/pagina/${c.documento.archivoId}/${c.documento.pagina}`}
              target="_blank"
              rel="noreferrer"
            >
              Ver la boleta
            </a>
          ) : null}
          {c.recorte ? (
            <a
              className="text-cyan-700 hover:underline dark:text-cyan-400"
              href={`/imagen/${c.recorte}`}
              target="_blank"
              rel="noreferrer"
            >
              Ver el voucher
            </a>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function Dato({
  titulo,
  valor,
  tono,
}: {
  titulo: string;
  valor: string;
  tono?: "ok" | "warn" | "bad";
}) {
  const color =
    tono === "ok"
      ? "text-emerald-600 dark:text-emerald-500"
      : tono === "warn"
        ? "text-amber-600 dark:text-amber-500"
        : tono === "bad"
          ? "text-red-600 dark:text-red-500"
          : "text-foreground";
  return (
    <div>
      <div className="font-semibold text-[10px] text-muted-foreground uppercase tracking-wider">
        {titulo}
      </div>
      <div
        className={cn("mt-0.5 font-semibold text-[15px] tabular-nums", color)}
      >
        {valor}
      </div>
    </div>
  );
}

function Lado({ titulo, filas }: { titulo: string; filas: string[][] }) {
  return (
    <div className="rounded-lg border bg-muted/30 p-4">
      <div className="mb-3 font-semibold text-[11px] text-muted-foreground uppercase tracking-wider">
        {titulo}
      </div>
      <dl className="space-y-2 text-[12.5px]">
        {filas.map(([k, v]) => (
          <div
            key={`${k}${v}`}
            className="flex items-baseline justify-between gap-4"
          >
            <dt className="text-muted-foreground">{k}</dt>
            <dd
              className={cn(
                "truncate text-right font-medium",
                // Los números de operación en ancho fijo: es lo que deja ver
                // que dos se diferencian en un dígito sin ir contándolos.
                k?.includes("operación") && "font-mono text-[12px]",
              )}
              title={v}
            >
              {v}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function dinero(centimos: number, moneda: "PEN" | "USD"): string {
  const signo = moneda === "USD" ? "US$" : "S/";
  return `${signo} ${(centimos / 100).toLocaleString("es-PE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fecha(iso: string): string {
  const [a, m, d] = iso.split("-");
  return `${d}-${m}-${a}`;
}

/**
 * Buscar la boleta correcta cuando el cruce propuso otra.
 *
 * Las que tienen un voucher del importe que se está mirando salen primero y
 * marcadas: es donde está la respuesta casi siempre, y ponerlas delante evita
 * teclear. Las demás se pueden elegir igual —a veces el pago correcto es de una
 * boleta que no tiene voucher leído—, pero sin la marca, para que se vea que
 * esa elección no la respalda ninguna evidencia.
 */
function BuscadorDeBoletas({
  importe,
  moneda,
  texto,
  alEscribir,
  alElegir,
  inhabilitado,
}: {
  importe: number;
  moneda: "PEN" | "USD";
  texto: string;
  alEscribir: (v: string) => void;
  alElegir: (comprobanteId: string) => void;
  inhabilitado: boolean;
}) {
  const { data, isPending } = api.buscarBoletas.useQuery(
    ["boletas", texto, String(importe)],
    { query: { texto: texto || undefined, importe, moneda } },
  );
  const boletas = data?.status === 200 ? data.body.boletas : [];

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="font-medium text-[13px]">Elegir la boleta a mano</h3>
        <input
          value={texto}
          onChange={(e) => alEscribir(e.target.value)}
          placeholder="Serie, número o cliente…"
          className="h-[34px] min-w-0 flex-1 rounded-md border bg-background px-2.5 text-[12.5px] outline-none placeholder:text-muted-foreground"
        />
      </div>

      <div className="mt-3 max-h-72 overflow-y-auto">
        {isPending ? (
          <Skeleton className="h-24 w-full" />
        ) : boletas.length === 0 ? (
          <p className="py-6 text-center text-[12.5px] text-muted-foreground">
            Ninguna boleta coincide con eso.
          </p>
        ) : (
          <table className="w-full text-[12.5px]">
            <tbody>
              {boletas.map((b: (typeof boletas)[number]) => (
                <tr key={b.id} className="border-b last:border-0">
                  <td className="py-2 font-medium">{b.serieNumero ?? "—"}</td>
                  <td className="max-w-xs truncate py-2 text-muted-foreground">
                    {b.cliente ?? "—"}
                  </td>
                  <td className="py-2 tabular-nums">
                    {b.fechaEmision ? fecha(b.fechaEmision) : "—"}
                  </td>
                  <td className="py-2">
                    {b.cuadra ? (
                      <Estado tono="ok">
                        tiene un voucher de este importe
                      </Estado>
                    ) : null}
                  </td>
                  <td className="py-2 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={inhabilitado}
                      onClick={() => alElegir(b.id)}
                    >
                      Enlazar
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
